import { FC } from 'react';
import { Avatar, Box, Button, Group, Modal, Stack, Text, Title, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconCalendar, IconClock, IconX } from '@tabler/icons-react';
import { MODAL_RADIUS, ONBOARDING_STAGES, OnboardingManager } from '@/PageComponents/Onboarding/onboarding-config';
import styles from '@/PageComponents/Onboarding/components/BeforeYouStartModal.module.css';

export interface BeforeYouStartModalProps {
    opened: boolean;
    onClose: () => void;
    onBookDemo: () => void;
    /** "Start exploring ServCraft" (the booked version), before the modal closes. */
    onStartExploring?: () => void;
    firstName: string;
    manager: OnboardingManager;
    /** The user already has a demo booked: talk them through it instead of asking them to book one ("Start exploring" closes). */
    demoBooked?: boolean;
    /** When the booked demo is, e.g. "tomorrow" (null when the date isn't known). */
    demoWhen?: string | null;
}

const BeforeYouStartModal: FC<BeforeYouStartModalProps> = ({ opened, onClose, onBookDemo, onStartExploring, firstName, manager, demoBooked = false, demoWhen = null }) => {
    const isMobile = useMediaQuery('(max-width: 800px)');
    const heroTitle = demoBooked ? (demoWhen ? `Your demo is ${demoWhen}` : 'Your demo is booked') : "You won't be onboarding alone.";
    const heroCopy = demoBooked ? (
        <>
            <Text className={styles.heroText}>We will match you with an account manager, who will help you set up your business.</Text>
            <Text className={`${styles.heroText} ${styles.light}`}>Make a head start before your demo and set up the basics.</Text>
        </>
    ) : (
        <>
            <Text className={styles.heroText}>Meet your account manager who will understand your business, get your setup and help you take control.</Text>
            <Text className={`${styles.heroText} ${styles.light}`}>See how it works in your team to get setup 2 x faster.</Text>
        </>
    );

    return (
        <Modal.Root
            opened={opened}
            onClose={onClose}
            size={1081}
            fullScreen={isMobile}
            // The design fits a 744px-tall laptop screen; Mantine's default 5dvh offset would make it scroll.
            yOffset={8}
            centered
            closeOnEscape
            closeOnClickOutside
            transitionProps={{ transition: 'pop', exitDuration: 50, duration: 100, timingFunction: 'ease' }}
            classNames={{ content: styles.content, body: styles.body }}
        >
            <Modal.Overlay color="var(--mantine-color-scBlue-5)" backgroundOpacity={0.55} blur={10} />
            <Modal.Content radius={MODAL_RADIUS} aria-label="Before you start">
            <Modal.Body p={0}>
            <Box className={styles.main}>
                <Group justify="space-between" wrap="nowrap" className={styles.header}>
                    <Text className={styles.welcome}>{firstName ? `Welcome to ServCraft, ${firstName}.` : 'Welcome to ServCraft.'}</Text>
                    <UnstyledButton id="onboarding-before-you-start-close" aria-label="Close" onClick={onClose} className={styles.close}>
                        <IconX size={isMobile ? 14 : 18} stroke={2.5} />
                    </UnstyledButton>
                </Group>

                <Box className={styles.inner}>
                    {demoBooked ? (
                        <div className={styles.bookedPill}><IconCalendar size={20} />Demo booked</div>
                    ) : (
                        <div className={styles.badge}><IconClock size={14} stroke={2.5} />Before you start</div>
                    )}
                    <Title order={2} className={styles.heading}>Here's what to expect.</Title>
                    <Text className={styles.lead}>A quick look at who you're working with and what happens during your trial.</Text>

                    <div className={styles.hero}>
                        {isMobile && !demoBooked && <Text className={styles.heroTitle}>{heroTitle}</Text>}
                        <div className={styles.heroRow}>
                            <Avatar src={manager.photoUrl} alt={manager.name} size={isMobile ? 75 : 144} radius="50%" color="scBlue" className={styles.photo}>
                                {manager.name.charAt(0)}
                            </Avatar>
                            {isMobile ? (
                                demoBooked ? (
                                    <Text className={styles.heroTitle}>{heroTitle}</Text>
                                ) : (
                                    <div className={styles.heroShort}>
                                        <Text inherit>ServCraft can help.</Text>
                                        <Text inherit className={styles.light}>Get up and running 3 x faster.</Text>
                                    </div>
                                )
                            ) : (
                                <Stack gap={16} className={styles.heroCopy}>
                                    <Text className={styles.heroTitle}>{heroTitle}</Text>
                                    {heroCopy}
                                </Stack>
                            )}
                        </div>
                        {isMobile && demoBooked && <Stack gap={8}>{heroCopy}</Stack>}
                        {demoBooked ? (
                            <Button id="onboarding-start-exploring" radius="xl" className={styles.exploreButton} onClick={() => { onStartExploring?.(); onClose(); }}>Start exploring ServCraft</Button>
                        ) : (
                            <Button id="onboarding-book-demo-before-you-start" color="green" radius="xl" className={styles.bookButton} leftSection={<IconCalendar size={isMobile ? 14 : 20} />} onClick={onBookDemo}>
                                Book a demo
                            </Button>
                        )}
                    </div>

                    <Text className={styles.stagesTitle}>
                        We work with your business across each stage.{isMobile && ' Best to complete on a desktop view.'}
                    </Text>
                    <ol className={styles.stages}>
                        {ONBOARDING_STAGES.map((stage, i) => (
                            <li key={stage.title} className={styles.stage}>
                                <div className={`${styles.stageNumber} ${i === 0 ? (demoBooked ? styles.stageNumberBooked : styles.stageNumberFirst) : ''}`}>{i + 1}</div>
                                <div className={styles.stageCopy}>
                                    <Text className={styles.stageTitle}>{stage.title}</Text>
                                    <Text className={styles.stageText}>{isMobile ? stage.short : stage.description}</Text>
                                </div>
                            </li>
                        ))}
                    </ol>
                </Box>
            </Box>
            </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
};

export default BeforeYouStartModal;
